import { useCallback, useEffect, useState } from 'react';
import { createSafeResizeObserver } from '@shared/utils/createSafeResizeObserver';
import { useIsomorphicLayoutEffect } from '@shared/utils/hooks/useIsomorphicLayoutEffect';

type UseFitTextProps = {
  textRef: React.RefObject<HTMLDivElement>;
  maxFontSize: number;
  widthOffset: number;
  heightOffset: number;
  minFontSize?: number;
};

/**
 * Custom hook to automatically adjust the font size of a text element so that it fits within its container.
 *
 * @param {Object} props - The properties for the hook.
 * @param {React.RefObject<HTMLDivElement>} props.textRef - Ref to the text element whose font size should be adjusted.
 * @param {number} props.maxFontSize - The maximum font size to use.
 * @param {number} props.widthOffset - The total horizontal offset (e.g., padding/margin) to subtract from the container's width.
 * @param {number} props.heightOffset - The total vertical offset (e.g., padding/margin) to subtract from the container's height.
 * @param {number} [props.minFontSize=10] - The minimum font size to use.
 *
 * @returns {{
 *   containerRef: React.RefObject<HTMLDivElement>,
 *   fontSize: number,
 *   lineCount: number
 * }} An object containing a ref to be attached to the container, the calculated font size, and the number of text lines.
 *
 * @example
 * const { containerRef, fontSize } = useFitText({
 *   textRef,
 *   maxFontSize: 100,
 *   widthOffset: 40,
 *   heightOffset: 20,
 * });
 *
 * return (
 *   <div ref={containerRef}>
 *     <div ref={textRef} style={{ fontSize }}>
 *       Some text
 *     </div>
 *   </div>
 * );
 */

export const useFitText = (
  args: UseFitTextProps,
): {
  containerRef: (el: HTMLDivElement | null) => void;
  fontSize: number;
  lineCount: number;
  /** `true` after the first font size calculation completes (client-side only). */
  isReady: boolean;
} => {
  const { textRef, maxFontSize, widthOffset, heightOffset, minFontSize = 10 } = args;

  const [fontMetrics, setFontMetrics] = useState({ fontSize: maxFontSize, lineCount: 1, isReady: false });

  // Callback ref so we get a re-render when the container mounts/unmounts
  // (e.g., when isCompactView toggles the conditional block).
  // A regular useRef wouldn't trigger recalculation when the element appears.
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const containerRef = useCallback((el: HTMLDivElement | null) => {
    setContainerEl(el);
  }, []);

  const calculateFontSize = useCallback(() => {
    if (!containerEl || !textRef.current) return;

    const textEl = textRef.current;

    const maxTextContainerHeight = containerEl.clientHeight - heightOffset;
    const maxTextContainerWidth = containerEl.clientWidth - widthOffset;

    // Quick check: if maxFontSize fits, skip the binary search entirely.
    textEl.style.fontSize = `${maxFontSize}px`;
    if (textEl.scrollHeight <= maxTextContainerHeight && textEl.scrollWidth <= maxTextContainerWidth) {
      const lineHeight = maxFontSize * 1.25;
      const lines = Math.max(1, Math.round(textEl.scrollHeight / lineHeight));
      setFontMetrics({ fontSize: maxFontSize, lineCount: lines, isReady: true });
      return;
    }

    // Binary search for the largest font size that fits within the container.
    // ~7 iterations for a 10-125px range vs ~115 with linear 1px decrements.
    // Integer bounds only: with a fractional bound (e.g. rem-scaled maxFontSize), mid
    // can equal low while high-low stays > 1, wedging this loop forever (PRNT-4909).
    // high is exclusive (must overflow): the quick check proved maxFontSize overflows,
    // so floor+1 (> maxFontSize) overflows too, keeping floor(maxFontSize) searchable.
    let low = Math.ceil(minFontSize);
    let high = Math.floor(maxFontSize) + 1;

    while (high - low > 1) {
      const mid = Math.floor((low + high) / 2);
      textEl.style.fontSize = `${mid}px`;

      const overflows = textEl.scrollHeight > maxTextContainerHeight || textEl.scrollWidth > maxTextContainerWidth;

      if (overflows) {
        high = mid;
      } else {
        low = mid;
      }
    }

    // Verify the final value fits (low is the largest non-overflowing size)
    textEl.style.fontSize = `${low}px`;
    const currentSize = low;

    // the height of a line of the text. 1.25 is the "line-height" value.
    const lineHeight = currentSize * 1.25;
    // the number of lines of the text. min 1 line.
    const lines = Math.max(1, Math.round(textEl.scrollHeight / lineHeight));

    setFontMetrics({ fontSize: currentSize, lineCount: lines, isReady: true });
  }, [containerEl, heightOffset, maxFontSize, minFontSize, textRef, widthOffset]);

  // useLayoutEffect runs synchronously after DOM mutations but BEFORE the browser paints.
  // This ensures the font size is set before the user sees a single frame, eliminating
  // the flash from the initial maxFontSize to the fitted value.
  useIsomorphicLayoutEffect(() => {
    calculateFontSize();
  }, [calculateFontSize]);

  // Observers for ongoing changes (container resize, text edits) can use regular useEffect
  // since subsequent updates don't need to block paint.
  useEffect(() => {
    const resizeObserver = createSafeResizeObserver(() => calculateFontSize());
    if (containerEl) {
      resizeObserver.observe(containerEl);
    }

    const mutationObserver = new MutationObserver(() => calculateFontSize());
    if (textRef.current) {
      mutationObserver.observe(textRef.current, {
        characterData: true,
        childList: true,
        subtree: true,
      });
    }

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [calculateFontSize, containerEl, textRef]);

  return { containerRef, fontSize: fontMetrics.fontSize, lineCount: fontMetrics.lineCount, isReady: fontMetrics.isReady };
};
